import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { TerminalRow } from './TerminalRow';

interface Release {
  version: string | null;
  sha256?: string;
  released_at?: string;
  release_notes?: string;
}

interface AgentStatus {
  agent_id: string;
  online: boolean;
  version: string | null;
  printers: Record<string, unknown>;
  last_heartbeat: string;
}

interface Terminal {
  id: string;
  description: string | null;
  seller_group_id: number;
  active: boolean;
}

export function PrintAgentPanel() {
  const [release, setRelease] = useState<Release | null>(null);
  const [agents, setAgents] = useState<Record<string, AgentStatus>>({});
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newId, setNewId] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const refresh = async () => {
    const [vRes, aRes, tRes] = await Promise.all([
      api.get<Release>('/settings/print-agent/version'),
      api.get<{ agents: Record<string, AgentStatus> }>('/settings/print-agent/agents'),
      api.get<Terminal[]>('/wh-terminals'),
    ]);
    if (vRes.success) setRelease((vRes.data as any) || null);
    if (aRes.success) setAgents((aRes.data as any)?.agents || {});
    if (tRes.success) setTerminals((tRes.data as any) || []);
  };

  useEffect(() => {
    refresh();
  }, []);

  const createTerminal = async () => {
    if (!newId) return;
    try {
      const r = await api.post('/wh-terminals', {
        id: newId,
        description: newDesc,
        seller_group_id: 1,
      });
      if (r.success) {
        setNewId('');
        setNewDesc('');
        setShowCreate(false);
        refresh();
      } else {
        alert(r.error || 'Error');
      }
    } catch (e: any) {
      alert(e.message);
    }
  };

  const onlineCount = Object.values(agents).filter((a) => a.online).length;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Print Agent</h2>

      {/* Download card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-2">Agente de impresión para Windows</h3>
        <p className="text-gray-600 mb-4">
          Instalalo en cada PC del depósito que tenga impresora Rollo conectada.
        </p>
        {release?.version ? (
          <>
            <div className="mb-3 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                v{release.version}
              </span>
              {release.released_at && (
                <span className="text-gray-500 text-sm">
                  {new Date(release.released_at).toLocaleString()}
                </span>
              )}
            </div>
            <a href="/api/settings/print-agent/download" download>
              <button className="bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700">
                ⬇ Descargar cbt-print-agent-{release.version}.exe
              </button>
            </a>
            {release.release_notes && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm text-gray-700">
                  Notas de la versión
                </summary>
                <pre className="mt-2 bg-gray-50 p-3 rounded text-xs whitespace-pre-wrap">
                  {release.release_notes}
                </pre>
              </details>
            )}
            <details className="mt-2">
              <summary className="cursor-pointer text-sm text-gray-700">
                Cómo instalar (Windows)
              </summary>
              <ol className="list-decimal ml-6 mt-2 text-sm space-y-1">
                <li>Descargá el .exe</li>
                <li>Ejecutalo como administrador (botón derecho → Ejecutar como admin)</li>
                <li>Se abre la pantalla de configuración en tu browser (localhost:39501)</li>
                <li>Elegí la impresora Rollo y poné el ID de terminal (ej: WH-PACK-01)</li>
                <li>Click "Guardar y arrancar"</li>
              </ol>
            </details>
          </>
        ) : (
          <div className="bg-yellow-50 p-4 rounded text-sm">
            No hay versión publicada todavía.
          </div>
        )}
      </div>

      {/* Agents online card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-3">
          Agentes online ({onlineCount}/{terminals.length})
        </h3>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-2">Terminal</th>
              <th className="text-left p-2">Estado</th>
              <th className="text-left p-2">Versión</th>
              <th className="text-left p-2">Impresoras</th>
              <th className="text-left p-2">Último heartbeat</th>
            </tr>
          </thead>
          <tbody>
            {terminals.map((t) => {
              const a = agents[t.id];
              const outdated =
                a && release?.version && a.version !== release.version;
              return (
                <tr key={t.id} className="border-b">
                  <td className="p-2 font-mono">{t.id}</td>
                  <td className="p-2">
                    {a?.online ? (
                      <span className="text-green-600">● online</span>
                    ) : (
                      <span className="text-gray-400">○ offline</span>
                    )}
                  </td>
                  <td className="p-2">
                    {a?.version || '—'}
                    {outdated && (
                      <span className="bg-yellow-100 text-yellow-800 ml-2 px-1 rounded text-xs">
                        actualizar
                      </span>
                    )}
                  </td>
                  <td className="p-2">
                    {a?.printers ? Object.keys(a.printers).length : 0}
                  </td>
                  <td className="p-2 text-gray-500">
                    {a?.last_heartbeat
                      ? new Date(a.last_heartbeat).toLocaleString()
                      : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Terminals CRUD card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold">Terminales registradas</h3>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
          >
            + Agregar terminal
          </button>
        </div>
        {showCreate && (
          <div className="bg-gray-50 p-3 rounded mb-3 flex gap-2">
            <input
              value={newId}
              onChange={(e) => setNewId(e.target.value)}
              placeholder="ID (ej: WH-PACK-02)"
              className="border rounded p-1 flex-1"
            />
            <input
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Descripción"
              className="border rounded p-1 flex-1"
            />
            <button
              onClick={createTerminal}
              className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
            >
              Crear
            </button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-2">ID</th>
              <th className="text-left p-2">Descripción</th>
              <th className="text-left p-2">Group</th>
              <th className="text-left p-2">Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {terminals.map((t) => (
              <TerminalRow key={t.id} terminal={t} onUpdate={refresh} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
